// Custom Figma Icons - exact matches from design
import {
  CompanyMeetingIcon,
  TrainingClassIcon,
  TeamMeetingIcon,
  ClientCallIcon,
  FaithGatheringListIcon,
  PresentationUploadIcon,
  DefaultMeetingIcon,
  OtherMeetingIcon,
} from "../components/Icons";

import smartAgendasContent from "../constants/smartAgendasContent.json";

/**
 * Maps icon string names to actual React icon components
 * Using custom SVG icons extracted from Figma design:
 * https://www.figma.com/design/BIVtusLkqwoVKG5izJRYS5/Townhall-Agenda-Redesign?node-id=130-1229&m=dev
 */
const iconComponentMap = {
  // Direct mapping to custom Figma-extracted icons
  CompanyMeetingIcon: CompanyMeetingIcon, // Company Meeting
  TrainingIcon: TrainingClassIcon, // Training/Class
  TeamMeetingIcon: TeamMeetingIcon, // Team Meeting
  ClientCallIcon: ClientCallIcon, // Client Call
  FaithGatheringIcon: FaithGatheringListIcon, // Faith Gathering
  UploadPresentationIcon: PresentationUploadIcon, // Presentation Upload
  FaQuestionCircle: OtherMeetingIcon, // Other
};

/**
 * Gets the React icon component for a given meeting type
 * @param {string} meetingType - The meeting type ID (e.g., 'team', 'company', 'training')
 * @returns {React.Component} - The corresponding icon component
 */
export const getMeetingTypeIcon = (meetingType) => {
  if (!meetingType) {
    return DefaultMeetingIcon; // Default fallback icon
  }

  // Find the meeting type in our content
  const meetingTypeData = smartAgendasContent.meetingTypes.find(
    (type) => type.id === meetingType
  );

  if (!meetingTypeData) {
    return DefaultMeetingIcon; // Default fallback icon
  }

  // Get the icon component from our map
  const IconComponent = iconComponentMap[meetingTypeData.icon];

  return IconComponent || DefaultMeetingIcon; // Fallback to default if icon not found
};

/**
 * Gets the meeting type color (consistent purple for all types)
 * Can be extended in the future for type-specific colors
 * @param {string} meetingType - The meeting type ID
 * @returns {string} - The color hex code
 */
export const getMeetingTypeColor = (meetingType) => {
  // For now, all meeting types use the same purple color
  // This can be extended in the future for type-specific colors
  return "#4C47CC";
};

/**
 * Gets the meeting type title for display
 * @param {string} meetingType - The meeting type ID
 * @returns {string} - The human-readable title
 */
export const getMeetingTypeTitle = (meetingType) => {
  if (!meetingType) {
    return "Meeting";
  }

  const meetingTypeData = smartAgendasContent.meetingTypes.find(
    (type) => type.id === meetingType
  );

  return meetingTypeData?.title || "Meeting";
};

/**
 * Checks if a meeting type is valid
 * @param {string} meetingType - The meeting type ID to validate
 * @returns {boolean} - Whether the meeting type exists
 */
export const isValidMeetingType = (meetingType) => {
  return smartAgendasContent.meetingTypes.some(
    (type) => type.id === meetingType
  );
};

/**
 * Gets all available meeting types with their icon components
 * Useful for dropdowns or selection components
 * @returns {Array} - Array of meeting type objects with icon components
 */
export const getAllMeetingTypes = () => {
  return smartAgendasContent.meetingTypes.map((type) => ({
    ...type,
    iconComponent: iconComponentMap[type.icon] || DefaultMeetingIcon,
  }));
};

const meetingTypeUtils = {
  getMeetingTypeIcon,
  getMeetingTypeColor,
  getMeetingTypeTitle,
  isValidMeetingType,
  getAllMeetingTypes,
};

export default meetingTypeUtils;
